import API from '../constant';
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

type UserRole = 'superuser' | 'school' | 'college' | 'university' | 'college_university' | null;

const AuthContext = createContext<{ 
    token: string | null;
    setToken: (newToken: string | null) => void;
    role: UserRole;
    setRole: (r: UserRole) => void;
}>({
    token: null,
    setToken: () => { },
    role: null,
    setRole: () => { },
});

type AuthProps = {
    children: React.ReactNode;   
}


const AuthProvider: React.FC<AuthProps> = ({ children }) => {
    const REMEMBER_KEY = 'auth_remember';
    const navigate = useNavigate();
    const location = useLocation();

    // Determine initial storage preference (default to remember to preserve existing behavior)
    const initialRemember = (typeof window !== 'undefined' && localStorage.getItem(REMEMBER_KEY) !== null)
      ? localStorage.getItem(REMEMBER_KEY) === '1'
      : true;

    // State to hold the authentication token
    const [token, setToken_] = useState<string | null>(() => {
        try {
            if (initialRemember) {
                return localStorage.getItem('token');
            }
            return sessionStorage.getItem('token') || localStorage.getItem('token');
        } catch {
            return null;
        }
    });

    // User role state (derived from login response and stored in storage)
    const [role, setRole] = useState<UserRole>(() => {
        try {
            return (localStorage.getItem('auth_role') as UserRole) || null;
        } catch {
            return null;
        }
    });

    // Function to set the authentication token
    const setToken = (newToken: string | null) => {
        setToken_(newToken);
    };

    useEffect(() => {
        const remember = localStorage.getItem(REMEMBER_KEY) === '1';
        if (token) {
            API.defaults.headers.common["Authorization"] = `Bearer ${token}`;
            try {
                if (remember) {
                    localStorage.setItem('token', token);
                    sessionStorage.removeItem('token');
                } else {
                    sessionStorage.setItem('token', token);
                    localStorage.removeItem('token');
                }
            } catch {}
        } else {
            delete API.defaults.headers.common["Authorization"];
            try {
                localStorage.removeItem('token');
                localStorage.removeItem('refresh');
                sessionStorage.removeItem('token');
                sessionStorage.removeItem('refresh');
            } catch {}
            if (location.pathname !== '/login') {
                navigate('/login');
            }
        }
    }, [token, navigate, location.pathname]);

    useEffect(() => {
        // Persist role for future sessions
        try {
            if (role) {
                localStorage.setItem('auth_role', role);
            } else {
                localStorage.removeItem('auth_role');
            }
        } catch {}
    }, [role]);

    // Memoized value of the authentication context
    const contextValue = useMemo(
        () => ({
            token,
            setToken,
            role,
            setRole,
        }),
        [token, role]
    );

    // Provide the authentication context to the children components
    return (
        <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};

export default AuthProvider;

